import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Search, Star, MapPin, Heart, Wifi, Car, Pool, ArrowRight, CheckCircle } from 'lucide-react';
import { useFavorites } from '../contexts/FavoritesContext';

const Home = () => {
  const { toggleFavorite, isFavorite } = useFavorites();
  const navigate = useNavigate();
  const [searchData, setSearchData] = useState({
    location: '',
    checkIn: '2024-04-15',
    checkOut: '2024-04-20'
  });

  const handleSearch = (e) => {
    e.preventDefault();
    
    // Basic validation
    if (!searchData.location.trim()) {
      alert('Please enter a location');
      return;
    }

    // Navigate to properties page with search parameters
    const queryParams = new URLSearchParams({
      location: searchData.location,
      checkIn: searchData.checkIn,
      checkOut: searchData.checkOut
    });

    navigate(`/properties?${queryParams.toString()}`);
  };
  
  const [featuredProperties, setFeaturedProperties] = useState([]);
  const [loading, setLoading] = useState(true);

  React.useEffect(() => {
    const fetchProperties = async () => {
      try {
        const { getPublicProperties } = require('../api/properties');
        const data = await getPublicProperties({ limit: 3 });
        if (data && Array.isArray(data)) {
          const formattedProperties = data.map(p => ({
            id: p.id,
            title: p.name,
            location: p.city ? `${p.city}, ${p.country || ''}` : p.address,
            price: p.pricePerNight,
            rating: p.rating || 0,
            reviews: Array.isArray(p.reviews) ? p.reviews.length : 0,
            image: (p.images && p.images.length > 0) ? p.images[0] : "https://images.unsplash.com/photo-1571896349842-33c89424de2d?w=400&h=300&fit=crop"
          }));
          setFeaturedProperties(formattedProperties.slice(0, 3));
        }
      } catch (error) {
        console.error('Failed to fetch featured properties:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProperties();
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-r from-blue-500 to-purple-400 text-white">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1571896349842-33c89424de2d?w=1600&q=85&auto=format&fit=crop"
            alt="Luxury beachfront vacation rental"
            className="w-full h-full object-cover"
            loading="eager"
            sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw"
          />
          {/* Dark Overlay for Text Readability */}
          <div className="absolute inset-0 bg-black bg-opacity-40"></div>
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <div className="text-center">
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              Find Your Perfect Getaway
            </h1>
            <p className="text-xl md:text-2xl mb-8 text-purple-100">
              Discover exceptional vacation rentals curated by Roar Homes
            </p>
            
            {/* Search Bar */}
            <form onSubmit={handleSearch} className="max-w-3xl mx-auto bg-white rounded-lg shadow-lg p-2">
              <div className="flex flex-wrap lg:flex-nowrap justify-around">
                <div className="flex-1 flex items-center px-4 py-3 md:max-w-[50%] lg:max-w-[50%]">
                  <Search className="text-gray-400 mr-3" size={20} />
                  <input
                    type="text"
                    placeholder="Where are you going?"
                    className="w-full text-gray-900 placeholder-gray-500 focus:outline-none"
                    value={searchData.location}
                    onChange={(e) => setSearchData({...searchData, location: e.target.value})}
                  />
                </div>
                <div className="px-3 border-l border-gray-200 md:max-w-[30%] lg:max-w-[25%]">
                  <label className="block text-xs font-semibold text-gray-700 mb-1">CHECK-IN</label>
                  <input 
                    type="date" 
                    className="w-24 text-xs sm:text-sm text-gray-900 bg-transparent border-none focus:outline-none lg:w-full" 
                    value={searchData.checkIn}
                    onChange={(e) => setSearchData({...searchData, checkIn: e.target.value})}
                  />
                </div>
                <div className="px-3 border-l border-gray-200 md:max-w-[30%] lg:max-w-[25%]">
                  <label className="block text-xs font-semibold text-gray-700 mb-1">CHECK-OUT</label>
                  <input 
                    type="date" 
                    className="w-24 text-xs sm:text-sm text-gray-900 bg-transparent border-none focus:outline-none lg:w-full" 
                    value={searchData.checkOut}
                    onChange={(e) => setSearchData({...searchData, checkOut: e.target.value})}
                  />
                </div>
                <button 
                  type="submit"
                  className="w-full lg:w-32 lg:flex-shrink-0 px-6 py-3 mt-2 lg:mt-0 bg-gradient-to-r from-pink-500 to-red-500 text-white font-semibold rounded-lg hover:from-pink-600 hover:to-red-600 transition-all duration-200"
                >
                  Search
                </button>
              </div>
            </form>
          </div>
        </div>
      </section>

       {/* Featured Properties */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center mb-12">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-2 sm:whitespace-nowrap">Featured Properties</h2>
              <p className="text-gray-600">Handpicked selections for an unforgettable experience</p>
            </div>
            <Link
              to="/properties"
              className="flex items-center text-pink-600 font-semibold hover:text-pink-700 transition-colors"
            >
              <span className="sm:hidden">View All</span>
              <span className="hidden sm:inline">View All Properties</span>
              <ArrowRight className="ml-1 sm:ml-2" size={18} />
            </Link>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {featuredProperties.map((property) => (
              <div key={property.id} className="property-card bg-white rounded-lg shadow-md overflow-hidden border border-gray-200">
                <div className="relative">
                  <Link to={`/property/${property.id}`}>
                    <img
                      src={property.image}
                      alt={property.title}
                      className="w-full h-48 object-cover"
                    />
                  </Link>
                  <button 
                    onClick={() => toggleFavorite(property)}
                    className="absolute top-3 right-3 p-2 bg-white rounded-full shadow-md hover:shadow-lg transition-shadow"
                  >
                    <Heart 
                      className={`w-4 h-4 transition-colors ${
                        isFavorite(property.id) ? "text-red-500 fill-red-500" : "text-gray-600"
                      }`} 
                    />
                  </button>
                </div>
                
                <div className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center text-sm text-gray-600">
                      <MapPin className="w-4 h-4 mr-1" />
                      {property.location}
                    </div>
                    <div className="flex items-center">
                      <Star className="w-4 h-4 text-yellow-400 mr-1" />
                      <span className="text-sm font-semibold">{property.rating}</span>
                      <span className="text-sm text-gray-600 ml-1">({property.reviews})</span>
                    </div>
                  </div>
                  
                  <Link to={`/property/${property.id}`}>
                    <h3 className="font-semibold text-gray-900 mb-2 hover:text-pink-600 transition-colors">
                      {property.title}
                    </h3>
                  </Link>
                  
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="text-2xl font-bold text-gray-900">₦{property.price?.toLocaleString()}</span>
                      <span className="text-gray-600 ml-1">/ night</span>
                    </div>
                    <Link
                      to={`/property/${property.id}`}
                      className="px-4 py-2 bg-gradient-to-r from-pink-500 to-red-500 text-white text-sm font-semibold rounded-lg hover:from-pink-600 hover:to-red-600 transition-all duration-200"
                    >
                      Book Now
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Why Choose Roar Homes?</h2>
            <p className="text-gray-600 text-lg">Experience the difference with our premium vacation rentals</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-r from-pink-500 to-red-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="text-white" size={32} />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Verified Properties</h3>
              <p className="text-gray-600">All our properties are personally inspected and verified for quality and comfort</p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-r from-pink-500 to-red-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <Heart className="text-white" size={32} />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Exceptional Service</h3>
              <p className="text-gray-600">24/7 customer support and concierge services to make your stay perfect</p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-r from-pink-500 to-red-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <Star className="text-white" size={32} />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Best Price Guarantee</h3>
              <p className="text-gray-600">Find a lower price? We'll match it and give you an additional discount</p>
            </div>
          </div>
        </div>
      </section>

     

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-purple-600 to-pink-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-4">Ready to Start Your Journey?</h2>
          <p className="text-xl mb-8 text-purple-100">
            Join thousands of satisfied travelers who found their perfect vacation rental with Roar Homes
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/properties"
              className="px-8 py-3 bg-white text-purple-600 font-semibold rounded-lg hover:bg-gray-100 transition-colors duration-200"
            >
              Explore Properties
            </Link>
            <Link
              to="/contact"
              className="px-8 py-3 border-2 border-white text-white font-semibold rounded-lg hover:bg-white hover:text-purple-600 transition-all duration-200"
            >
              Contact Us
            </Link>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div>
              <div className="text-3xl font-bold text-pink-600 mb-2">500+</div>
              <div className="text-gray-600">Premium Properties</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-pink-600 mb-2">50K+</div>
              <div className="text-gray-600">Happy Guests</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-pink-600 mb-2">100+</div>
              <div className="text-gray-600">Destinations</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-pink-600 mb-2">4.9</div>
              <div className="text-gray-600">Average Rating</div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
